import * as React from 'react';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { ImportHelp, ColumnDefinition } from './ImportHelpComponent';
import { FileInput } from './ui/FileInput';
import { RenderErrors } from './ui/ErrorComponent';
import { VotingRightAccountImportState } from '../_generatedTypes.g';
import { Translator } from '../Translator';
import { ReadAs, api } from '../api';
import { useDispatch } from 'react-redux';
import { getTranslator } from '../Translator';
import { useState } from 'react';
import { voterEntityStateRefreshed } from '../features/innerState/innerStateSlice';
import { useAppSelector } from '../hooks';

export enum ImportResultStatus {
    Idle = 0,
    Importing = 1,
    ImportingSucces = 2,
    ImportingError = 3,
    Saving = 4,
    SavingSuccess = 5,
    SavingError = 6,
    SavingTimeout = 7,
}

export enum SpecialDataType {
    Date = 1,
    Boolean = 2,
}


function colKey<T>(col: ColumnDefinition<T>) {
    return String(col.propertyName) + col.language;
}

const votingRightColumnDefinitions = (t: Translator): ColumnDefinition<VotingRightAccountImportState>[] => [
    { columnTitle: t['User name'], formatDescription: `${t['Text']} (max 450)`, requiredYesOrNo: t['Yes'], propertyName: 'UserName', maxLength: 450 },
    { columnTitle: t['Election Code'], formatDescription: `${t['Text']} (max 50)`, requiredYesOrNo: t['Yes'], propertyName: 'ElectionCode', maxLength: 50 },
];

export const VoterImport = () => {
    const language = useAppSelector(state => state.language);
    const token = useAppSelector(state => state.clientState.RequestVerificationToken);
    const dispatch = useDispatch();
    const t = getTranslator(language);

    const [status, setStatus] = useState<ImportResultStatus>(ImportResultStatus.Idle);
    const [errors, setErrors] = useState<string[]>([]);
    const [numberAdded, setNumberAdded] = useState<number>(0);
    const [numberUpdated, setNumberUpdated] = useState<number>(0);
    const [changes, setChanges] = useState<string[]>([]);
    const [entities, setEntities] = useState<VotingRightAccountImportState[]>(new Array<VotingRightAccountImportState>());

    const columnDefinitions = votingRightColumnDefinitions(t);

    const uploadExcel = async (formData: FormData) => {
        setStatus(ImportResultStatus.Importing);
        return await api(token).VotingRights.Parse<void>(formData)
            .ok(entities => {
                setStatus(ImportResultStatus.ImportingSucces);
                setEntities(entities)
            },
                ReadAs.Json)
            .anyStatus(errors => {
                setStatus(ImportResultStatus.ImportingError);
                setErrors(errors)
            },
                ReadAs.ValidationErrors)
            .fail(() => setStatus(ImportResultStatus.ImportingError));
    };

    const saveParsedEntities = async (parsedEntities: VotingRightAccountImportState[]) => {
        setStatus(ImportResultStatus.Saving);
        return await api(token).VotingRights.AddRange<void>(parsedEntities)
            .ok(importResultState => {
                dispatch(voterEntityStateRefreshed(importResultState.FullFreshEntities));
                setStatus(ImportResultStatus.SavingSuccess);
                setNumberAdded(importResultState.NumberAdded);
                setNumberUpdated(importResultState.NumberUpdated);
                setChanges(importResultState.Changes);
            },
                ReadAs.Json)
            .status(400,
                errors => {
                    setStatus(ImportResultStatus.SavingError);
                    setErrors(errors)
                },
                ReadAs.ValidationErrors)
            .anyStatus((text: string) => {
                // App Service has a hard request timeout of 230 seconds.
                const status = text.indexOf('The request timed out') !== -1
                    ? ImportResultStatus.SavingTimeout
                    : ImportResultStatus.SavingError;
                setStatus(status);
            },
                ReadAs.Text)
            .fail(() => setStatus(ImportResultStatus.SavingError));
    };

    return <div>
        <h1>{t['Manage voting rights']}</h1>
        <div className="uploadForm">
            <form method="post" encType="multipart/form-data" onSubmit={e => {
                e.preventDefault();
                uploadExcel(new FormData(e.currentTarget));
            }}>
                <ImportHelp
                    <VotingRightAccountImportState>
                    importObjectName={t['Voting right'].toLowerCase()}
                    importObjectNamePlural={t['Voting rights'].toLowerCase()}
                    t={t}
                    columnDefinitions={columnDefinitions} />
                <h3>{t['Import voting rights']}</h3>
                <br />
                <FileInput name="uplExcelFile" accept=".xlsx" caption={t['Select input file']} />
                <br /><input id="btnSubmit" type="submit" value={t['Parse data']} />
                <br />
            </form>
            <div>{(() => {
                if (status === ImportResultStatus.ImportingSucces && entities.length > 0) {
                    return <div className="import-preview">
                        <div className="import-table">
                            <table>
                                <thead>
                                    <tr>
                                        {columnDefinitions.map(col => <th key={colKey(col)}>{col.language ? t.columnDescription(col.columnTitle, col.language, col.maxLength || 0) : col.columnTitle}</th>)}
                                    </tr>
                                </thead>
                                <tbody >
                                    {entities.map((entity, i) => <tr key={i}>
                                        {columnDefinitions.map(col => {

                                            const tdClass = col.isSpecialDataType === SpecialDataType.Date ? "dateColumn" : undefined;
                                            const propValue = entity[col.propertyName];

                                            const displayValue = col.isSpecialDataType === SpecialDataType.Date
                                                ? moment(propValue.toString()).format('L')
                                                : col.isSpecialDataType === SpecialDataType.Boolean
                                                    ? (propValue.toString() === 'true' ? t['Yes'] : t['No'])
                                                    : col.language && propValue.hasOwnProperty(col.language)
                                                        ? (propValue as any)[col.language] //deze any slaat nergens op: of het type van entity is gewoon fout, of deze ternary is niet reachable
                                                        : propValue.toString();

                                            return <td key={colKey(col)} lang={col.language} className={tdClass}>{displayValue}</td>;
                                        })}
                                    </tr>)}
                                </tbody>
                            </table>
                        </div>
                        <div className="statusbanner--ok">{t['Parsing succeeded']}</div>
                        <input type="button" value={t['Import voting rights']} onClick={() => saveParsedEntities(entities)} />
                        <br />
                    </div>;
                } else if (status === ImportResultStatus.Importing) {
                    return <div className="statusbanner--ok">{t['Parsing']}...</div>;
                } else if (status === ImportResultStatus.ImportingError) {
                    return <div className="statusbanner--warning">
                        {t['Parsing error']}
                        <RenderErrors errors={errors} />
                    </div>;
                } else if (status === ImportResultStatus.SavingError) {
                    return <div className="statusbanner--warning">
                        {t['An error occurred during saving.']}
                        <RenderErrors errors={errors} />
                    </div>;
                } else if (status === ImportResultStatus.Saving) {
                    return <div className="statusbanner--ok">{t['Saving...']}</div>;
                } else if (status === ImportResultStatus.SavingTimeout) {
                    return <div className="statusbanner--warning">
                        {t['It is not clear if the import was successful. Please wait a few minutes and use an export to verify if the data is present.']}
                    </div>;
                } else if (status === ImportResultStatus.SavingSuccess) {
                    return <div className="import-preview">
                        {t['Number added']}: {numberAdded}<br />
                        {t['Number changed']}: {numberUpdated}<br />
                        <br />
                        {(numberUpdated !== 0) &&
                            <>
                                {t['Changed']}:<br />
                                {changes.map(change => <>{change}<br /></>)}
                            </>}
                        <div className="statusbanner--ok">{t['Your changes were successfully saved.']}</div>
                    </div>;
                } else {
                    return null;
                }
            })()}</div>
            <br /><Link className="linkbutton" to={'/admin'}>{t['Back']}</Link>
        </div>
    </div>;
};
